import { OpsApiError } from '../api/client.ts';
import { useT } from '../i18n/index.ts';
import { Chip } from '../ui/index.ts';

export function isTimelineUnsampled(input: {
  readonly timeline_unsampled?: true;
  readonly events: readonly unknown[];
  readonly metric: { readonly sampled?: boolean | null };
}): boolean {
  return input.timeline_unsampled === true
    || (input.events.length === 0 && input.metric.sampled === false);
}

/**
 * The lifecycle detail answered 404 `REQUEST_LIFECYCLE_NOT_FOUND`: no metrics
 * row. With `LIFECYCLE_METRICS_SAMPLED=1` (#2937) that is the normal answer
 * for an unsampled success (only kept requests get a row), and otherwise a
 * request past retention — an absence to explain, not an error to alarm on.
 */
export function isLifecycleNotRecorded(error: unknown): boolean {
  return error instanceof OpsApiError
    && error.status === 404
    && error.code === 'REQUEST_LIFECYCLE_NOT_FOUND';
}

/** Neutral stand-in for the lifecycle panel when {@link isLifecycleNotRecorded}. */
export function LifecycleNotRecordedNotice({ testId, className }: {
  readonly testId: string;
  readonly className: string;
}): JSX.Element {
  const t = useT();
  return (
    <div className={className} data-testid={testId}>
      {t('request_lifecycle.not_recorded')}
    </div>
  );
}

function formatSampleRate(sampleRate: number | null | undefined): string {
  return sampleRate === null || sampleRate === undefined ? '—' : String(sampleRate);
}

export function LifecycleSamplingMarker({
  metric,
  events,
  timelineUnsampled,
  testId,
}: {
  readonly metric: {
    readonly sampled?: boolean | null;
    readonly sampleRate?: number | null;
  };
  readonly events: readonly unknown[];
  readonly timelineUnsampled?: true;
  readonly testId: string;
}): JSX.Element | null {
  const t = useT();
  if (isTimelineUnsampled({ timeline_unsampled: timelineUnsampled, events, metric })) {
    return (
      <Chip tone="warn" lamp={false} testId={testId}>
        {t('request_lifecycle.timeline.unsampled', { p: formatSampleRate(metric.sampleRate) })}
      </Chip>
    );
  }
  if (metric.sampled === true && metric.sampleRate !== null && metric.sampleRate !== undefined && metric.sampleRate < 1) {
    return (
      <span className="text-micro text-paper-low" data-testid={`${testId}-kept`}>
        {t('request_lifecycle.timeline.kept', { p: formatSampleRate(metric.sampleRate) })}
      </span>
    );
  }
  return null;
}
